import { useEffect, useRef, useState } from 'react'

import { useQuery, useScreenId, useIsHydrating, useIsScreenLoading } from '@muse-web/router'

import { isGoogleEntryOfTab } from '@core/analytics'
import { getNextjsOrigin } from '@core/backend'
import { DIALOG_IDS, modalStore, type TDialogId } from '@core/modal'
import { PlanType } from '@core/models'
import { type ScreenId } from '@core/router'
import {
    claimSessionDialogSlot,
    giveBackNextjsExitVisit,
    isExcludedScreen,
    isExternalEntry,
    isNextjsDestination,
    pickSessionDialog,
    readNextjsExitClick,
    readSessionDialogState,
    readVisitsCounter,
    SESSION_DIALOG_IDS,
    sessionDialogStore,
    useLcpRecorded,
    writeOldUsersPromoShownAtVisit,
    writeVisitsCounter,
    type TSessionDialogContext,
    type TSessionDialogId,
} from '@core/session-dialogs'
import { useActions, useValues } from '@core/store'
import { CHECKOUT_PARAMS } from '@features/checkout'
import {
    OLD_USERS_PAYWALL_REQUEST,
    usePaywallDialog,
    useShowCreatorProfileOffer,
    type TPaywallRequest,
} from '@features/paywall'
import { useCurrentUser } from '@features/session'
import { usePlan } from '@features/subscription'

import { SESSION_DIALOG_DEFINITIONS } from './sessionDialogDefinitions'
import { useCreatorOfferContext } from './useCreatorOfferContext'

const NAVIGATION_EVENTS = ['popstate', 'pushState', 'replaceState']

type TSessionDialogOpeners = {
    openModal: (id: TDialogId) => void
    openPaywall: (request: TPaywallRequest) => void
    showCreatorOffer: () => void
}

const buildShow = ({
    openModal,
    openPaywall,
    showCreatorOffer,
}: TSessionDialogOpeners): Record<TSessionDialogId, () => void> => ({
    [SESSION_DIALOG_IDS.creatorProfileOffer]: showCreatorOffer,
    [SESSION_DIALOG_IDS.converterPromo]: () => openModal(DIALOG_IDS.converterPromo),
    [SESSION_DIALOG_IDS.oldUsersPromo]: () => openPaywall(OLD_USERS_PAYWALL_REQUEST),
    [SESSION_DIALOG_IDS.anniversaryPromo]: () => openModal(DIALOG_IDS.anniversaryPromo),
})

export const SessionDialogEvaluator = () => {
    const isHydrating = useIsHydrating()
    const isScreenLoading = useIsScreenLoading()
    const screenId = useScreenId<ScreenId>()
    const query = useQuery<{ productId?: string }>()
    const { isAuthenticated, isRefreshing: isSessionRefreshing, joinDate } = useCurrentUser()
    const { data: plan, isLoading: isPlanLoading } = usePlan()
    const lcpRecorded = useLcpRecorded()
    const { open: openPaywall } = usePaywallDialog()
    const showCreatorOffer = useShowCreatorProfileOffer()
    const { isCandidate, isChecking, isAssignmentSettled, isBlocked } = useCreatorOfferContext()

    const [isFromGoogle] = useState(() => isGoogleEntryOfTab(getNextjsOrigin()))
    const [hasCheckoutRedirect, setHasCheckoutRedirect] = useState(
        () => query[CHECKOUT_PARAMS.productId] !== undefined,
    )

    useEffect(() => {
        const handleNavigation = () => {
            setHasCheckoutRedirect(
                new URLSearchParams(window.location.search).has(CHECKOUT_PARAMS.productId),
            )
        }

        NAVIGATION_EVENTS.forEach(event => window.addEventListener(event, handleNavigation))

        return () => {
            NAVIGATION_EVENTS.forEach(event => window.removeEventListener(event, handleNavigation))
        }
    }, [])

    const [activeDialog, { exhausted, deferred }] = useValues([
        modalStore.props.getActiveDialog,
        sessionDialogStore.getRoot,
    ])

    const { claimSlot, clearDeferred, openModal, setPendingNextjsExit, giveBackExitVisit } =
        useActions({
            claimSlot: claimSessionDialogSlot,
            clearDeferred: sessionDialogStore.actions.clearDeferred,
            openModal: modalStore.actions.open,
            setPendingNextjsExit: sessionDialogStore.actions.setPendingNextjsExit,
            giveBackExitVisit: giveBackNextjsExitVisit,
        })

    const [startedAsGuest] = useState(!isAuthenticated)
    const hasCountedVisit = useRef(false)

    useEffect(() => {
        if (hasCountedVisit.current || !isAuthenticated) {
            return
        }

        hasCountedVisit.current = true

        if (!startedAsGuest && !isExternalEntry(getNextjsOrigin())) {
            return
        }

        writeVisitsCounter(readVisitsCounter() + 1)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated])

    useEffect(() => {
        if (!isAuthenticated) {
            return undefined
        }

        const handleClick = (event: MouseEvent) => {
            const exit = readNextjsExitClick(event)

            if (!exit || !isNextjsDestination(exit.href, getNextjsOrigin())) {
                return
            }

            if (exit.opensTab) {
                return
            }

            setPendingNextjsExit(true)
        }

        const handlePageHide = () => {
            giveBackExitVisit()
        }

        document.addEventListener('click', handleClick)
        window.addEventListener('pagehide', handlePageHide)

        return () => {
            document.removeEventListener('click', handleClick)
            window.removeEventListener('pagehide', handlePageHide)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated])

    useEffect(() => {
        if (isHydrating || isScreenLoading || hasCheckoutRedirect || exhausted || !screenId) {
            return
        }

        const context: TSessionDialogContext = {
            screenId,
            isAuthenticated,
            isSessionRefreshing,
            planType: plan?.type ?? null,
            isPlanLoading,
            joinDate,
            now: Date.now(),
            lcpRecorded,
            isFromGoogle,
            visitsCounter: readVisitsCounter(),
            ...readSessionDialogState(),
            writeOldUsersPromoShownAtVisit,
            creatorOffer: { isCandidate, isChecking, isAssignmentSettled, isBlocked },
        }

        const winner = pickSessionDialog(SESSION_DIALOG_DEFINITIONS, context)

        if (!winner) {
            return
        }

        claimSlot({
            id: winner.id,
            show: buildShow({ openModal, openPaywall, showCreatorOffer })[winner.id],
        })
    }, [
        isHydrating,
        isScreenLoading,
        hasCheckoutRedirect,
        exhausted,
        screenId,
        isAuthenticated,
        isSessionRefreshing,
        plan?.type,
        isPlanLoading,
        joinDate,
        lcpRecorded,
        isFromGoogle,
        isCandidate,
        isChecking,
        isAssignmentSettled,
        isBlocked,
        showCreatorOffer,
        claimSlot,
        openModal,
        openPaywall,
    ])

    useEffect(() => {
        if (deferred === null || activeDialog !== null || isScreenLoading) {
            return
        }

        clearDeferred()

        if (plan?.type && plan.type !== PlanType.Free) {
            return
        }

        if (screenId && isExcludedScreen(screenId)) {
            return
        }

        claimSlot({
            id: deferred,
            show: buildShow({ openModal, openPaywall, showCreatorOffer })[deferred],
        })
    }, [
        deferred,
        activeDialog,
        isScreenLoading,
        screenId,
        plan?.type,
        clearDeferred,
        claimSlot,
        openModal,
        openPaywall,
        showCreatorOffer,
    ])

    return null
}
